import AuthErrorBoundary from '@/components/AuthErrorBoundary';
import AwsInitializer from '@/components/AwsInitializer';
import ConfigurationError from '@/components/ConfigurationError';
import { AuthProvider as CustomAuthProvider } from '@/contexts/AuthContext';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';

// Validate required environment variables
const requiredViteEnvVars = {
  VITE_USER_POOL_ID: import.meta.env.VITE_USER_POOL_ID,
  VITE_USER_POOL_CLIENT_ID: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  VITE_AWS_REGION: import.meta.env.VITE_AWS_REGION,
  VITE_GUARDRAIL_ID: import.meta.env.VITE_GUARDRAIL_ID,
  VITE_BEDROCK_AGENT_CORE_ARN: import.meta.env.VITE_BEDROCK_AGENT_CORE_ARN,
  
};

const requiredEnvVars = {
  ...requiredViteEnvVars,
  userPoolId: requiredViteEnvVars.VITE_USER_POOL_ID,
  clientId: requiredViteEnvVars.VITE_USER_POOL_CLIENT_ID,
  region: requiredViteEnvVars.VITE_AWS_REGION,
  agentArn: requiredViteEnvVars.VITE_BEDROCK_AGENT_CORE_ARN
};

// Check for missing configuration
const missingVars = Object.entries(requiredViteEnvVars)
  .filter(([_, value]) => !value || value === '')
  .map(([key]) => key);

if (missingVars.length > 0) {
  console.error('Missing required environment variables:', missingVars);
  console.error('Please run ./scripts/update-frontend-env.sh after CDK deployment');
}

// Cognito OIDC configuration optimized for transparent authentication
const cognitoAuthConfig = {
  authority: `https://cognito-idp.${requiredEnvVars.region}.amazonaws.com/${requiredEnvVars.userPoolId}`,
  client_id: requiredEnvVars.clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'email openid profile',
  automaticSilentRenew: true,
  loadUserInfo: true,
  // Optimized for less disruptive authentication
  monitorSession: false, // Disable session monitoring to prevent loops
  checkSessionInterval: 300000, // Check session every 5 minutes instead of 1 minute
  silentRequestTimeout: 10000, // Reduced timeout to 10 seconds
  accessTokenExpiringNotificationTime: 120, // Notify 2 minutes before expiration (reduced from 5)
  // Reduced retry count to prevent excessive attempts
  silentRenewRetryCount: 1,
  // Include additional metadata for better debugging
  includeIdTokenInSilentRenew: true,
  // Handle popup/iframe issues
  popupWindowFeatures: 'location=no,toolbar=no,width=500,height=500,left=100,top=100',
  // Add extra query parameters for debugging
  extraQueryParams: {
    prompt: 'none' // For silent renewal
  },
  // Add flag to reduce loading state flickering
  filterProtocolClaims: true,
  // Reduce the frequency of user info loading
  loadUserInfoOnSilentRenew: false
};

// Render the application
const root = createRoot(document.getElementById('root')!);

if (missingVars.length > 0) {
  // Show configuration error if environment variables are missing
  root.render(
    <StrictMode>
      <ConfigurationError missingVars={missingVars} />
    </StrictMode>
  );
} else {
  // Normal application render with authentication
  root.render(
    <StrictMode>
      <AuthErrorBoundary>
        <AuthProvider {...cognitoAuthConfig}>
          <BrowserRouter>
            <CustomAuthProvider>
              <AwsInitializer>
                <App />
              </AwsInitializer>
            </CustomAuthProvider>
          </BrowserRouter>
        </AuthProvider>
      </AuthErrorBoundary>
    </StrictMode>
  );
}